import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  Hash,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { Button } from "@/ui/components/Button";
import { Card } from "@/ui/components/Card";
import { Callout } from "@/ui/components/Callout";
import { useAuth } from "@/lib/auth/hooks";
import { getAuthCookie } from "@/lib/auth/session-cookie";
import { useBooleanFeatureFlag, useNumberFeatureFlag } from "@/app/lib/featureFlags/hooks";
import {
  fetchCertificationModule,
  fetchTraining,
  checkTrainingAnswer,
  submitTrainingAttempt,
  patchTrainingVideoProgress,
} from "@/lib/api/certifications";
import type {
  CertificationModuleDetail,
  CertificationTraining,
  CertificationTrainingQuestion,
  CertificationTrainingAttempt,
} from "@/lib/api/certifications";
import { isYoutubeUrl } from "@/app/lib/media/youtube";
import {
  estimateLessonMinutes,
  formatCourseDurationParts,
} from "@/app/lib/media/courseDuration";
import { useI18n } from "@/app/lib/i18n";
import { contentText, contentTextNode } from "@/app/lib/i18n/contentText";
import { resolveCourseCardImageUrl } from "@/app/components/certification/CertificationCourseCard";
import { vars } from "@/ui/styles/theme.css";
import { YoutubeLessonPlayer } from "../components/YoutubeLessonPlayer";
import { Html5LessonPlayer } from "../components/Html5LessonPlayer";
import type { PlayTimeProgress } from "@/app/lib/media/playTimeAccumulator";
import * as s from "./CertificationContent.css";

type SummaryPreview = "actual" | "passed" | "failed" | "courseComplete";

const SHUFFLE_QUIZ_QUESTIONS_FLAG = "certification.shuffle_quiz_questions";
const REQUIRE_VIDEO_WATCH_FLAG = "certification.require_video_watch_percent";
const VIDEO_PROGRESS_HEARTBEAT_MS = 5000;

type QuizPageProps = {
  courseId: string;
  lessonId: string;
};

type QuizTraining = Omit<CertificationTraining, "questions"> & {
  questions: CertificationTrainingQuestion[];
};

function shuffled<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const current = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = current;
  }
  return copy;
}

function toQuizTraining(training: QuizTraining, shuffle: boolean): QuizTraining {
  const { questions, ...rest } = training;
  return { ...rest, questions: shuffle ? shuffled(questions) : [...questions] };
}

type AnswerRecord = {
  questionId: string;
  choiceIndex: number;
  correct: boolean;
};

type NavDirection = "forward" | "back";

type ChoiceMotionPhase = "idle" | "exiting" | "entering";

type FlowPhase = "video" | "quiz";

type TransitionTarget = "question" | "result" | "video" | "quiz";

type ChoiceSnapshot = {
  title: string;
  choices: string[];
  selectedChoice: number | null;
  feedback: "correct" | "incorrect" | null;
};

/** Next incomplete training after `currentId` in course order. Does not wrap. */
function findNextSectionId(
  trainings: CertificationTraining[],
  currentId: string,
): string | null {
  const sorted = [...trainings].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber,
  );
  const idx = sorted.findIndex((t) => t.id === currentId);
  if (idx < 0) return null;
  return sorted.slice(idx + 1).find((t) => !t.userProgress?.passed)?.id ?? null;
}

function formatCompletedOn(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

function formatDurationLabel(
  t: (key: string) => string,
  totalMinutes: number,
): string {
  const parts = formatCourseDurationParts(totalMinutes);
  if (parts.unit === "minutes") {
    return `${parts.value} ${t("certification.course.minutesUnit")}`;
  }
  if (parts.remainderMinutes) {
    return `${parts.value} ${t("certification.course.hoursUnit")} ${parts.remainderMinutes} ${t("certification.course.minutesUnit")}`;
  }
  return `${parts.value} ${t("certification.course.hoursUnit")}`;
}

const VIDEO_CARD_MAX = 800;
const VIDEO_MOTION_MS = 320;
/** Space kept between the expanded transcript box and the bottom of the page. */
const TRANSCRIPT_BOTTOM_MARGIN_PX = 32;
const TRANSCRIPT_MIN_HEIGHT_PX = 160;

function choiceMotionDurationMs(count: number): number {
  return (
    s.QUIZ_CHOICE_MOTION_MS +
    Math.max(0, count - 1) * s.QUIZ_CHOICE_STAGGER_MS +
    40
  );
}

/** Break a transcript into readable blocks instead of one wall of text. */
function transcriptBlocks(raw: string): string[] {
  const normalized = raw.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  const byBlank = normalized
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
  if (byBlank.length > 1) return byBlank;

  if (normalized.includes("\n")) {
    return normalized
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  const sentences =
    normalized.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g)?.map((part) => part.trim()).filter(Boolean)
    ?? [normalized];
  if (sentences.length <= 2) return [normalized];

  const chunks: string[] = [];
  for (let i = 0; i < sentences.length; i += 2) {
    chunks.push(sentences.slice(i, i + 2).join(" "));
  }
  return chunks;
}

function QuizHeroLayout({
  children,
  wide,
  courseId,
  courseTitle,
  trainingTitle,
  heroImageSrc,
  debugSlot,
}: {
  children: React.ReactNode;
  wide?: boolean;
  courseId: string;
  courseTitle?: React.ReactNode;
  trainingTitle?: React.ReactNode;
  heroImageSrc?: string | null;
  debugSlot?: React.ReactNode;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const hasHeroTitles = Boolean(courseTitle || trainingTitle);
  const goToCourse = () => navigate({ to: `/course/${courseId}` } as any);
  const backLabel =
    t("certification.course.back").replace(/^[←<\u2190\-–—]+\s*/u, "").trim() ||
    "Back";

  return (
    <div className={s.quizPage}>
      <div
        className={s.quizHero}
        style={
          heroImageSrc
            ? { backgroundImage: `url(${JSON.stringify(heroImageSrc)})` }
            : undefined
        }
      >
        <div className={s.quizHeroOverlay} aria-hidden />
        <button type="button" className={s.quizHeroBack} onClick={goToCourse}>
          <ArrowLeft size={18} />
          {backLabel}
        </button>
        {debugSlot}
        {hasHeroTitles ? (
          <div className={s.quizHeroTitleBlock}>
            {courseTitle ? (
              <p className={s.quizHeroCourseTitle}>{courseTitle}</p>
            ) : null}
            {trainingTitle ? (
              <h1 className={s.quizHeroTrainingTitle}>{trainingTitle}</h1>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={[s.quizOverlap, wide ? s.quizOverlapWide : ""].filter(Boolean).join(" ")}>
        {children}
      </div>
    </div>
  );
}

function QuizProgressRing({ percent }: { percent: number }) {
  const size = 56;
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const offset = circumference * (1 - clamped / 100);

  return (
    <svg
      className={s.quizProgressRing}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${clamped}%`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={vars.color.border}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={vars.color.primary}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.35s ease" }}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize="12"
        fontWeight="700"
        fill={vars.color.fg}
      >
        {clamped}%
      </text>
    </svg>
  );
}

function QuizResultStatusIcon({ passed }: { passed: boolean }) {
  const size = 56;
  return (
    <span
      className={
        passed ? s.quizResultStatusIconPass : s.quizResultStatusIconFail
      }
      role="img"
      aria-label={passed ? "Passed" : "Failed"}
    >
      {passed ? <CheckCircle2 size={size} strokeWidth={1.75} /> : <XCircle size={size} strokeWidth={1.75} />}
    </span>
  );
}

export function QuizPage({ courseId, lessonId }: QuizPageProps) {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const token = accessToken ?? getAuthCookie("access_token");
  const firstName = user?.firstName?.trim() || "";
  const shuffleQuestions = useBooleanFeatureFlag(
    SHUFFLE_QUIZ_QUESTIONS_FLAG,
    true,
  );
  const requireWatchPercent = Math.max(
    0,
    Math.min(100, Math.round(useNumberFeatureFlag(REQUIRE_VIDEO_WATCH_FLAG, 0))),
  );
  const [summaryPreview, setSummaryPreview] =
    React.useState<SummaryPreview>("actual");

  const [training, setTraining] = React.useState<QuizTraining | null>(null);
  const [moduleTrainings, setModuleTrainings] = React.useState<CertificationTraining[] | null>(null);
  const [moduleTitle, setModuleTitle] = React.useState<string | null>(null);
  const [moduleHeroImageUrl, setModuleHeroImageUrl] = React.useState<string | null>(null);
  const [moduleTrophyImageUrl, setModuleTrophyImageUrl] = React.useState<string | null>(null);
  const [moduleEnrollment, setModuleEnrollment] = React.useState<
    CertificationModuleDetail["enrollment"] | null
  >(null);
  const [moduleSlug, setModuleSlug] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [flowPhase, setFlowPhase] = React.useState<FlowPhase>("video");
  const [transcriptOpen, setTranscriptOpen] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<AnswerRecord[]>([]);
  const [selectedChoice, setSelectedChoice] = React.useState<number | null>(null);
  const [feedback, setFeedback] = React.useState<"correct" | "incorrect" | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [result, setResult] = React.useState<CertificationTrainingAttempt | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [motionPhase, setMotionPhase] = React.useState<ChoiceMotionPhase>("idle");
  const [motionDirection, setMotionDirection] = React.useState<NavDirection>("forward");
  const [motionGeneration, setMotionGeneration] = React.useState(0);
  const [transitionTarget, setTransitionTarget] = React.useState<TransitionTarget | null>(null);
  const [outgoing, setOutgoing] = React.useState<ChoiceSnapshot | null>(null);
  const [titleHeightPx, setTitleHeightPx] = React.useState<number | null>(null);
  const [titleHeightMs, setTitleHeightMs] = React.useState(s.QUIZ_CHOICE_MOTION_MS);
  const [titleHeightAnimating, setTitleHeightAnimating] = React.useState(false);
  const [titleFadeMs, setTitleFadeMs] = React.useState(s.QUIZ_CHOICE_MOTION_MS);
  const answersRef = React.useRef(answers);
  answersRef.current = answers;
  const motionTimersRef = React.useRef<number[]>([]);
  const titleWrapRef = React.useRef<HTMLDivElement>(null);
  const titleInnerRef = React.useRef<HTMLDivElement>(null);
  const watchedSecondsRef = React.useRef(0);
  const durationSecondsRef = React.useRef(0);
  const [watchedPercent, setWatchedPercent] = React.useState(0);
  const [trackingUnavailable, setTrackingUnavailable] = React.useState(false);
  const transcriptPanelRef = React.useRef<HTMLDivElement>(null);
  const [transcriptFillHeightPx, setTranscriptFillHeightPx] = React.useState<
    number | null
  >(null);

  // Fill the rest of the viewport (with a comfortable bottom margin) instead
  // of a fixed max-height — recomputed on open and on resize.
  React.useLayoutEffect(() => {
    if (!transcriptOpen) return;
    const recompute = () => {
      const top = transcriptPanelRef.current?.getBoundingClientRect().top;
      if (top == null) return;
      const available = window.innerHeight - top - TRANSCRIPT_BOTTOM_MARGIN_PX;
      setTranscriptFillHeightPx(Math.max(TRANSCRIPT_MIN_HEIGHT_PX, available));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [transcriptOpen]);

  const clearMotionTimers = React.useCallback(() => {
    for (const id of motionTimersRef.current) window.clearTimeout(id);
    motionTimersRef.current = [];
  }, []);

  React.useEffect(() => () => clearMotionTimers(), [clearMotionTimers]);

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    setIndex(0);
    setAnswers([]);
    setSelectedChoice(null);
    setFeedback(null);
    setResult(null);
    setSummaryPreview("actual");
    setTranscriptOpen(false);
    setModuleTitle(null);
    setModuleHeroImageUrl(null);
    setModuleTrophyImageUrl(null);
    setModuleEnrollment(null);
    setModuleSlug(null);
    setMotionPhase("idle");
    setOutgoing(null);
    setTransitionTarget(null);
    setTitleHeightPx(null);
    setTitleHeightAnimating(false);
    setWatchedPercent(0);
    setTrackingUnavailable(false);
    watchedSecondsRef.current = 0;
    durationSecondsRef.current = 0;
    clearMotionTimers();
    Promise.all([
      fetchTraining(token, lessonId),
      fetchCertificationModule(token, courseId).catch(() => null),
    ])
      .then(([tr, mod]) => {
        setTraining(toQuizTraining(tr, shuffleQuestions));
        setModuleTrainings(mod?.trainings ?? null);
        setModuleTitle(mod?.title ?? null);
        setModuleHeroImageUrl(mod?.heroImageUrl ?? null);
        setModuleTrophyImageUrl(mod?.trophyImageUrl ?? null);
        setModuleEnrollment(mod?.enrollment ?? null);
        setModuleSlug(mod?.slug ?? null);
        setFlowPhase(tr.videoUrl ? "video" : "quiz");
        const seededSeconds = tr.watchedSeconds ?? 0;
        watchedSecondsRef.current = seededSeconds;
        durationSecondsRef.current = tr.videoDurationSeconds ?? 0;
        setWatchedPercent(tr.videoWatchedPercent ?? 0);
        setTrackingUnavailable(false);
      })
      .catch((err) => setError(err?.message || t("certification.quiz.notFound")))
      .finally(() => setLoading(false));
  }, [token, lessonId, courseId, t, clearMotionTimers]);

  const question = training?.questions[index];
  const isLastQuestion = !!training && index === training.questions.length - 1;
  const questionCount = training?.questions.length ?? 0;
  const hasVideo = Boolean(training?.videoUrl);
  // Progress = share of questions that already have a locked answer.
  const quizPercent =
    questionCount > 0 ? (answers.length / questionCount) * 100 : 0;

  const applyQuestionState = React.useCallback(
    (questionId: string, records: AnswerRecord[]) => {
      const existing = records.find((a) => a.questionId === questionId);
      if (existing) {
        setSelectedChoice(existing.choiceIndex);
        setFeedback(existing.correct ? "correct" : "incorrect");
      } else {
        setSelectedChoice(null);
        setFeedback(null);
      }
    },
    [],
  );

  const finishEnter = React.useCallback(
    (enterMs: number, fromHeight: number | null, holdExtraMs = 0) => {
      setTitleHeightMs(enterMs);
      setTitleFadeMs(enterMs);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const toHeight = titleInnerRef.current?.offsetHeight ?? fromHeight;
          if (toHeight != null) {
            setTitleHeightAnimating(true);
            setTitleHeightPx(toHeight);
          }
        });
      });
      const enterTimer = window.setTimeout(() => {
        setMotionPhase("idle");
        setTransitionTarget(null);
        setTitleHeightAnimating(false);
        setTitleHeightPx(null);
      }, enterMs + holdExtraMs);
      motionTimersRef.current.push(enterTimer);
    },
    [],
  );

  const navigateWithTransition = React.useCallback(
    (nextIndex: number, direction: NavDirection) => {
      if (!training || !question || motionPhase !== "idle") return;
      const target = training.questions[nextIndex];
      if (!target) return;

      clearMotionTimers();

      // Lock current title height so the card doesn't jump during exit.
      const fromHeight = titleWrapRef.current?.getBoundingClientRect().height ?? null;
      setTitleHeightAnimating(false);
      if (fromHeight != null) setTitleHeightPx(fromHeight);

      setOutgoing({
        title: question.text,
        choices: question.choices,
        selectedChoice,
        feedback,
      });
      setMotionDirection(direction);
      setTransitionTarget("question");
      setMotionPhase("exiting");
      const exitMs = choiceMotionDurationMs(question.choices.length);
      setTitleFadeMs(exitMs);

      const exitTimer = window.setTimeout(() => {
        setIndex(nextIndex);
        applyQuestionState(target.id, answersRef.current);
        setOutgoing(null);
        setMotionGeneration((g) => g + 1);
        setMotionPhase("entering");
        finishEnter(choiceMotionDurationMs(target.choices.length), fromHeight);
      }, exitMs);
      motionTimersRef.current.push(exitTimer);
    },
    [
      training,
      question,
      motionPhase,
      selectedChoice,
      feedback,
      clearMotionTimers,
      applyQuestionState,
      finishEnter,
    ],
  );

  const persistVideoProgress = React.useCallback(() => {
    if (!token || !lessonId) return;
    const seconds = watchedSecondsRef.current;
    if (seconds <= 0) return;
    const durationSeconds = durationSecondsRef.current;
    void patchTrainingVideoProgress(
      token,
      lessonId,
      seconds,
      durationSeconds > 0 ? durationSeconds : undefined,
    ).catch(() => {
      // Best-effort; local percent still gates Start Quiz.
    });
  }, [token, lessonId]);

  const handleWatchProgress = React.useCallback((progress: PlayTimeProgress) => {
    watchedSecondsRef.current = Math.max(
      watchedSecondsRef.current,
      progress.watchedSeconds,
    );
    if (progress.durationSeconds > 0) {
      durationSecondsRef.current = progress.durationSeconds;
    }
    setWatchedPercent((prev) => Math.max(prev, progress.percent));
  }, []);

  React.useEffect(() => {
    if (flowPhase !== "video") return;
    const id = window.setInterval(persistVideoProgress, VIDEO_PROGRESS_HEARTBEAT_MS);
    return () => {
      window.clearInterval(id);
      persistVideoProgress();
    };
  }, [flowPhase, persistVideoProgress]);

  const goToQuizFromVideo = React.useCallback(() => {
    if (!training || motionPhase !== "idle" || flowPhase !== "video") return;
    const first = training.questions[0];
    if (!first) return;
    const gateActive = Boolean(training.videoUrl) && requireWatchPercent > 0;
    if (gateActive && (trackingUnavailable || watchedPercent < requireWatchPercent)) {
      return;
    }
    persistVideoProgress();

    clearMotionTimers();
    setTranscriptOpen(false);

    const fromHeight = titleWrapRef.current?.getBoundingClientRect().height ?? null;
    setTitleHeightAnimating(false);
    if (fromHeight != null) setTitleHeightPx(fromHeight);

    setMotionDirection("forward");
    setTransitionTarget("quiz");
    setMotionPhase("exiting");
    setTitleFadeMs(VIDEO_MOTION_MS);

    const exitTimer = window.setTimeout(() => {
      setFlowPhase("quiz");
      setIndex(0);
      applyQuestionState(first.id, answersRef.current);
      setOutgoing(null);
      setMotionGeneration((g) => g + 1);
      setMotionPhase("entering");
      // Hold transition classes long enough for the delayed progress fade.
      finishEnter(choiceMotionDurationMs(first.choices.length), fromHeight, 140);
    }, VIDEO_MOTION_MS);
    motionTimersRef.current.push(exitTimer);
  }, [
    training,
    motionPhase,
    flowPhase,
    requireWatchPercent,
    trackingUnavailable,
    watchedPercent,
    persistVideoProgress,
    clearMotionTimers,
    applyQuestionState,
    finishEnter,
  ]);

  const goToVideoFromQuiz = React.useCallback(() => {
    if (!training || !question || !hasVideo || motionPhase !== "idle") return;
    if (flowPhase !== "quiz" || index !== 0) return;

    clearMotionTimers();

    const fromHeight = titleWrapRef.current?.getBoundingClientRect().height ?? null;
    setTitleHeightAnimating(false);
    if (fromHeight != null) setTitleHeightPx(fromHeight);

    setOutgoing({
      title: question.text,
      choices: question.choices,
      selectedChoice,
      feedback,
    });
    setMotionDirection("back");
    setTransitionTarget("video");
    setMotionPhase("exiting");
    const exitMs = choiceMotionDurationMs(question.choices.length);
    setTitleFadeMs(exitMs);

    const exitTimer = window.setTimeout(() => {
      setFlowPhase("video");
      setOutgoing(null);
      setMotionGeneration((g) => g + 1);
      setMotionPhase("entering");
      finishEnter(VIDEO_MOTION_MS, fromHeight);
    }, exitMs);
    motionTimersRef.current.push(exitTimer);
  }, [
    training,
    question,
    hasVideo,
    motionPhase,
    flowPhase,
    index,
    selectedChoice,
    feedback,
    clearMotionTimers,
    finishEnter,
  ]);

  const handleSelect = async (choiceIndex: number) => {
    if (!token || !question || selectedChoice !== null || motionPhase !== "idle") return;
    setSelectedChoice(choiceIndex);
    setChecking(true);
    try {
      const result = await checkTrainingAnswer(token, lessonId, question.id, choiceIndex);
      const correct = result.correct;
      setFeedback(correct ? "correct" : "incorrect");
      setAnswers((prev) => {
        const next: AnswerRecord = {
          questionId: question.id,
          choiceIndex,
          correct,
        };
        const existingIdx = prev.findIndex((a) => a.questionId === question.id);
        if (existingIdx >= 0) {
          const copy = [...prev];
          copy[existingIdx] = next;
          return copy;
        }
        return [...prev, next];
      });
    } catch (err: any) {
      setError(err?.message || t("certification.quiz.checkFailed"));
      setSelectedChoice(null);
      setFeedback(null);
    } finally {
      setChecking(false);
    }
  };

  const handlePrevious = () => {
    if (index <= 0 || motionPhase !== "idle") return;
    navigateWithTransition(index - 1, "back");
  };

  const handleNext = async () => {
    if (!token || !training || !question || motionPhase !== "idle") return;
    if (!isLastQuestion) {
      navigateWithTransition(index + 1, "forward");
      return;
    }

    // Finish quiz — submit, then transition into the summary view.
    setSubmitting(true);
    let attempt: CertificationTrainingAttempt;
    try {
      attempt = await submitTrainingAttempt(
        token,
        lessonId,
        answers.map(({ questionId, choiceIndex }) => ({ questionId, choiceIndex })),
      );
      if (attempt.passed) {
        try {
          const mod = await fetchCertificationModule(token, courseId);
          setModuleTrainings(mod.trainings);
          setModuleTrophyImageUrl(mod.trophyImageUrl ?? null);
          setModuleEnrollment(mod.enrollment ?? null);
        } catch {
          // Keep prior moduleTrainings; next CTA may be unavailable.
        }
      }
    } catch (err: any) {
      setError(err?.message || t("certification.quiz.submitFailed"));
      setSubmitting(false);
      return;
    } finally {
      setSubmitting(false);
    }

    clearMotionTimers();
    const fromHeight = titleWrapRef.current?.getBoundingClientRect().height ?? null;
    setTitleHeightAnimating(false);
    if (fromHeight != null) setTitleHeightPx(fromHeight);

    setOutgoing({
      title: question.text,
      choices: question.choices,
      selectedChoice,
      feedback,
    });
    setResult(attempt);
    setMotionDirection("forward");
    setTransitionTarget("result");
    setMotionPhase("exiting");
    const exitMs = choiceMotionDurationMs(question.choices.length);
    setTitleFadeMs(exitMs);

    const exitTimer = window.setTimeout(() => {
      setOutgoing(null);
      setMotionGeneration((g) => g + 1);
      setMotionPhase("entering");
      finishEnter(exitMs, fromHeight);
    }, exitMs);
    motionTimersRef.current.push(exitTimer);
  };

  // Note: cast — this page is mounted under the standalone certification
  // app's own route tree (src/certification-app/routes.tsx), not the main
  // app's globally-registered router. See routes.tsx.
  const backToCourse = () => navigate({ to: `/course/${courseId}` } as any);

  const trainingsWithPass = React.useMemo(() => {
    if (!moduleTrainings) return null;
    if (!result?.passed) return moduleTrainings;
    return moduleTrainings.map((tr) =>
      tr.id === lessonId
        ? {
            ...tr,
            userProgress: {
              passed: true,
              score: result.score,
              correctCount: result.correctCount,
              totalCount: result.totalCount,
              attemptedAt: result.attemptedAt,
            },
          }
        : tr,
    );
  }, [result, moduleTrainings, lessonId]);

  const nextIncompleteId = React.useMemo(() => {
    if (!result?.passed || !trainingsWithPass) return null;
    return findNextSectionId(trainingsWithPass, lessonId);
  }, [result, trainingsWithPass, lessonId]);

  const isCourseComplete = Boolean(
    result?.passed &&
      trainingsWithPass &&
      trainingsWithPass.length > 0 &&
      trainingsWithPass.every((tr) => tr.userProgress?.passed),
  );

  const goToNextSection = () => {
    if (!nextIncompleteId) return;
    navigate({ to: `/course/${courseId}/lesson/${nextIncompleteId}/quiz` } as any);
  };

  const handleRetake = () => {
    if (!training) return;
    clearMotionTimers();
    setTraining((prev) => (prev ? toQuizTraining(prev, shuffleQuestions) : prev));
    setIndex(0);
    setAnswers([]);
    setSelectedChoice(null);
    setFeedback(null);
    setResult(null);
    setFlowPhase("quiz");
    setMotionPhase("idle");
    setOutgoing(null);
    setTransitionTarget(null);
    setTitleHeightPx(null);
    setTitleHeightAnimating(false);
    setError(null);
    setMotionGeneration((g) => g + 1);
  };

  if (loading) {
    return <div className={s.pageWide}>{tx("shared.common.loading")}</div>;
  }

  if (error && !training) {
    return (
      <div className={s.pageWide}>
        <p>{error}</p>
        <Link to={`/course/${courseId}` as "/"}>{tx("certification.quiz.backToCourse")}</Link>
      </div>
    );
  }

  if (!training || training.questions.length === 0) {
    return (
      <div className={s.pageWide}>
        <p>{tx("certification.quiz.noQuestions")}</p>
        <Link to={`/course/${courseId}` as "/"}>{tx("certification.quiz.backToCourse")}</Link>
      </div>
    );
  }

  // Keep the last question visible while its answers exit; swap to summary on enter.
  const showingResult =
    result != null &&
    !(transitionTarget === "result" && motionPhase === "exiting");
  // Keep the video mounted through its exit so it can fade out; swap off when quiz enters.
  const showingVideo =
    !showingResult &&
    (flowPhase === "video" ||
      (transitionTarget === "video" && motionPhase === "entering"));
  const showingQuiz = !showingResult && !showingVideo;
  // Widen before the embed mounts (during quiz→video exit) and stay wide until
  // the video has unmounted — resizing a live YouTube player flashes its size badge.
  const wideColumn =
    showingVideo ||
    (transitionTarget === "video" && motionPhase === "exiting");

  const isResultTransition = transitionTarget === "result";
  const videoChromeFadeOut =
    transitionTarget === "quiz" && motionPhase === "exiting" ? s.quizFadeOut : "";
  const videoChromeFadeIn =
    transitionTarget === "video" && motionPhase === "entering" ? s.quizFadeIn : "";
  const quizChromeFadeOut =
    (isResultTransition || transitionTarget === "video") &&
    motionPhase === "exiting"
      ? s.quizFadeOut
      : "";
  // After video→quiz, wait for the embed to unmount/layout before fading chrome in.
  const quizChromeFadeIn =
    transitionTarget === "quiz" && motionPhase === "entering"
      ? s.quizFadeInDelayed
      : "";
  const resultChromeFadeIn =
    isResultTransition && motionPhase === "entering" ? s.quizFadeIn : "";

  const fadeVarStyle = {
    ["--quiz-title-fade-ms" as string]: `${titleFadeMs}ms`,
    ["--quiz-chrome-fade-delay-ms" as string]: "140ms",
  } as React.CSSProperties;

  const videoGateActive = Boolean(training?.videoUrl) && requireWatchPercent > 0;
  const videoGateBlocked =
    videoGateActive &&
    (trackingUnavailable || watchedPercent < requireWatchPercent);

  const choiceView: ChoiceSnapshot = outgoing ?? {
    title: question!.text,
    choices: question!.choices,
    selectedChoice,
    feedback,
  };
  const choiceLastIndex = Math.max(0, choiceView.choices.length - 1);
  const transitioning = motionPhase !== "idle";
  const displayFeedback = choiceView.feedback;
  const displayResult = (() => {
    if (!result || !training) return result;
    if (summaryPreview === "actual") return result;
    const total = training.questions.length;
    if (summaryPreview === "failed") {
      return {
        ...result,
        passed: false,
        correctCount: 0,
        totalCount: total,
        score: 0,
      };
    }
    return {
      ...result,
      passed: true,
      correctCount: total,
      totalCount: total,
      score: 100,
    };
  })();

  const showNextSection = Boolean(
    showingResult &&
      displayResult?.passed &&
      nextIncompleteId &&
      summaryPreview !== "courseComplete" &&
      summaryPreview !== "failed",
  );
  const showRetake = Boolean(
    showingResult && displayResult && !displayResult.passed,
  );
  const showCourseComplete = Boolean(
    showingResult &&
      displayResult?.passed &&
      (summaryPreview === "courseComplete" ||
        (summaryPreview === "actual" && isCourseComplete)),
  );
  const splitResultActions = showNextSection || showRetake;
  const courseMinutes = (moduleTrainings ?? []).reduce(
    (sum, tr) =>
      sum +
      estimateLessonMinutes({
        videoDurationSeconds: tr.videoDurationSeconds,
        questionCount: tr.questionCount ?? tr.questions?.length ?? 0,
      }),
    0,
  );
  const durationLabel = formatDurationLabel(t, courseMinutes);
  const completedOnIso =
    moduleEnrollment?.completedAt ??
    result?.attemptedAt ??
    (showCourseComplete ? new Date().toISOString() : null);
  const certNumber =
    moduleEnrollment?.certificateNumber?.trim() ||
    (summaryPreview === "courseComplete" ? "PREVIEW" : null);
  const reviewItems = showingResult
    ? training.questions.map((q) => {
        const rec = answers.find((a) => a.questionId === q.id);
        const chosen =
          rec && rec.choiceIndex >= 0 && rec.choiceIndex < q.choices.length
            ? q.choices[rec.choiceIndex]
            : q.choices[0] ?? null;
        const previewCorrect =
          summaryPreview === "passed" || summaryPreview === "courseComplete"
            ? true
            : summaryPreview === "failed"
              ? false
              : rec?.correct ?? false;
        return {
          id: q.id,
          text: q.text,
          correct: previewCorrect,
          chosen,
        };
      })
    : [];
  const youtube = isYoutubeUrl(training.videoUrl);
  const transcriptText = contentText(t, training.transcript);
  const transcriptParas = transcriptBlocks(transcriptText);
  const videoCommentaryText = contentText(t, training.videoCommentary);
  const videoCommentaryParas = transcriptBlocks(videoCommentaryText);

  const titleClassName = [
    s.quizQuestionTitle,
    motionPhase === "exiting" ? s.quizQuestionTitleExiting : "",
    motionPhase === "entering" ? s.quizQuestionTitleEntering : "",
  ]
    .filter(Boolean)
    .join(" ");

  const videoSlotClass = [
    s.quizVideoSlot,
    motionPhase === "exiting" ? s.quizVideoSlotExiting : "",
    motionPhase === "entering" ? s.quizVideoSlotEntering : "",
  ]
    .filter(Boolean)
    .join(" ");

  const progressHidden = showingVideo ? s.quizChromeInvisible : "";

  return (
    <QuizHeroLayout
      wide={wideColumn}
      courseId={courseId}
      heroImageSrc={resolveCourseCardImageUrl(moduleHeroImageUrl, moduleSlug)}
      courseTitle={
        moduleTitle ? contentTextNode(tx, moduleTitle) : undefined
      }
      trainingTitle={
        training?.title ? contentTextNode(tx, training.title) : undefined
      }
      debugSlot={
        showingResult ? (
          <label className={s.quizHeroDebug}>
            <span className={s.quizHeroDebugLabel}>{t("certification.quiz.debugPreview")}</span>
            <select
              className={s.quizHeroDebugSelect}
              value={summaryPreview}
              onChange={(e) =>
                setSummaryPreview(e.target.value as SummaryPreview)
              }
            >
              <option value="actual">{t("certification.quiz.debugActual")}</option>
              <option value="passed">{t("certification.quiz.debugPassed")}</option>
              <option value="failed">{t("certification.quiz.debugFailed")}</option>
              <option value="courseComplete">{t("certification.quiz.debugCourseComplete")}</option>
            </select>
          </label>
        ) : null
      }
    >
      <Card
        maxWidth={VIDEO_CARD_MAX}
        className={[
          s.quizCard,
          s.quizStraddleCard,
          showingVideo ? s.quizStraddleCardVideo : "",
          showCourseComplete ? s.quizStraddleCardComplete : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={showingVideo ? s.quizCardStack : undefined}>
          <div
            className={[
              s.quizCardHeaderZone,
              showingVideo ? s.quizChromeUnderVideo : "",
              showCourseComplete ? s.quizCardHeaderZoneComplete : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className={s.quizCardHeader} style={fadeVarStyle}>
              {showingResult && displayResult ? (
                <>
                  <span className={resultChromeFadeIn || undefined}>
                    {showCourseComplete && moduleTrophyImageUrl ? (
                      <img
                        className={s.quizResultTrophy}
                        src={moduleTrophyImageUrl}
                        alt=""
                      />
                    ) : (
                      <QuizResultStatusIcon passed={displayResult.passed} />
                    )}
                  </span>
                  <p
                    className={[s.quizProgress, resultChromeFadeIn]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {tx("certification.quiz.scoreSummary", {
                      correct: String(displayResult.correctCount),
                      total: String(displayResult.totalCount),
                      score: String(displayResult.score),
                    })}
                  </p>
                </>
              ) : (
                <>
                  <span
                    className={[
                      progressHidden,
                      quizChromeFadeOut,
                      quizChromeFadeIn,
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined}
                  >
                    <QuizProgressRing
                      percent={showingVideo ? 0 : quizPercent}
                    />
                  </span>
                  <p
                    className={[
                      s.quizProgress,
                      progressHidden,
                      quizChromeFadeOut,
                      quizChromeFadeIn,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {t("certification.quiz.questionIndex", {
                      current: showingVideo ? 1 : index + 1,
                      total: training.questions.length,
                    })}
                  </p>
                </>
              )}
            </div>
          </div>
          {error && showingQuiz ? <Callout variant="error">{error}</Callout> : null}
          <div
            ref={titleWrapRef}
            className={s.quizQuestionTitleWrap}
            style={
              titleHeightPx == null
                ? undefined
                : {
                    height: titleHeightPx,
                    transition: titleHeightAnimating
                      ? `height ${titleHeightMs}ms cubic-bezier(0.4, 0, 0.2, 1)`
                      : "none",
                  }
            }
          >
            <div ref={titleInnerRef}>
              {showingVideo ? (
                <div className={videoSlotClass} style={fadeVarStyle}>
                  {training.videoUrl ? (
                    youtube ? (
                      <YoutubeLessonPlayer
                        videoUrl={training.videoUrl}
                        initialWatchedSeconds={watchedSecondsRef.current}
                        onEnded={() => undefined}
                        onWatchProgress={handleWatchProgress}
                        onPersist={persistVideoProgress}
                        onTrackingUnavailable={() => setTrackingUnavailable(true)}
                      />
                    ) : (
                      <Html5LessonPlayer
                        src={training.videoUrl}
                        initialWatchedSeconds={watchedSecondsRef.current}
                        onWatchProgress={handleWatchProgress}
                        onPersist={persistVideoProgress}
                      />
                    )
                  ) : (
                    <div className={s.videoPlaceholder}>
                      {tx("certification.lesson.noVideo")}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h2
                    className={[
                      titleClassName,
                      showCourseComplete ? s.quizResultWellDoneTitle : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={fadeVarStyle}
                  >
                    {showingResult && displayResult
                      ? displayResult.passed
                        ? showCourseComplete
                          ? firstName
                            ? t("certification.quiz.wellDoneNamed", {
                                name: firstName,
                              })
                            : t("certification.quiz.wellDone")
                          : t("certification.quiz.passed")
                        : t("certification.quiz.failed")
                      : contentTextNode(tx, choiceView.title)}
                  </h2>
                  {showCourseComplete ? (
                    <p className={s.quizResultWellDoneSubtitle}>
                      {t("certification.quiz.wellDoneSubtitle")}
                    </p>
                  ) : null}
                  {showingResult && displayResult ? (
                    <>
                      {showCourseComplete ? (
                        <>
                          <div className={s.quizResultCourseBlock}>
                            <div className={s.quizResultCourseCopy}>
                              <p className={s.quizResultCourseLabel}>
                                {tx("certification.quiz.courseCompleted")}
                              </p>
                              <p className={s.quizResultCourseTitle}>
                                {moduleTitle
                                  ? contentTextNode(tx, moduleTitle)
                                  : null}
                              </p>
                            </div>
                            <QuizProgressRing percent={100} />
                          </div>
                          <div className={s.quizResultChips}>
                            {completedOnIso ? (
                              <div className={s.quizResultChip}>
                                <span
                                  className={[
                                    s.quizResultChipIcon,
                                    s.quizResultChipIconCompleted,
                                  ].join(" ")}
                                >
                                  <CalendarCheck size={28} />
                                </span>
                                <div className={s.quizResultChipCopy}>
                                  <p className={s.quizResultChipLabel}>
                                    {tx("certification.quiz.completedOn")}
                                  </p>
                                  <p className={s.quizResultChipValue}>
                                    {formatCompletedOn(completedOnIso)}
                                  </p>
                                </div>
                              </div>
                            ) : null}
                            {courseMinutes > 0 ? (
                              <div className={s.quizResultChip}>
                                <span
                                  className={[
                                    s.quizResultChipIcon,
                                    s.quizResultChipIconDuration,
                                  ].join(" ")}
                                >
                                  <Clock size={28} />
                                </span>
                                <div className={s.quizResultChipCopy}>
                                  <p className={s.quizResultChipLabel}>
                                    {tx("certification.quiz.duration")}
                                  </p>
                                  <p className={s.quizResultChipValue}>
                                    {durationLabel}
                                  </p>
                                </div>
                              </div>
                            ) : null}
                            {certNumber ? (
                              <div className={s.quizResultChip}>
                                <span
                                  className={[
                                    s.quizResultChipIcon,
                                    s.quizResultChipIconCert,
                                  ].join(" ")}
                                >
                                  <Hash size={28} />
                                </span>
                                <div className={s.quizResultChipCopy}>
                                  <p className={s.quizResultChipLabel}>
                                    {tx("certification.quiz.certificationNumber")}
                                  </p>
                                  <p className={s.quizResultChipValue}>
                                    {certNumber}
                                  </p>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </>
                      ) : null}
                      {!showCourseComplete ? (
                      <div className={s.quizReviewList}>
                        <p className={s.quizReviewHeading}>
                          {tx("certification.quiz.reviewHeading")}
                        </p>
                        {reviewItems.map((item) => (
                          <div
                            key={item.id}
                            className={[
                              s.quizReviewItem,
                              item.correct
                                ? s.quizReviewItemCorrect
                                : s.quizReviewItemIncorrect,
                            ].join(" ")}
                          >
                            <span
                              className={s.quizReviewIcon}
                              style={{
                                color: item.correct ? "#16A34A" : vars.color.danger,
                              }}
                            >
                              {item.correct ? (
                                <CheckCircle2 size={18} />
                              ) : (
                                <XCircle size={18} />
                              )}
                            </span>
                            <div className={s.quizReviewCopy}>
                              <p className={s.quizReviewQuestion}>
                                {contentTextNode(tx, item.text)}
                              </p>
                              {item.chosen ? (
                                <p className={s.quizReviewAnswer}>
                                  {t("certification.quiz.yourAnswer", {
                                    answer: contentText(t, item.chosen),
                                  })}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                      ) : null}
                      <div
                        className={[
                          splitResultActions
                            ? s.quizResultActionsSplit
                            : s.quizResultActionsCenter,
                          resultChromeFadeIn,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={fadeVarStyle}
                      >
                        <div className={s.quizResultAction}>
                          <Button
                            iconOnly
                            size="lg"
                            variant="outline"
                            color="neutral"
                            aria-label={t("certification.quiz.backToCourseShort")}
                            title={t("certification.quiz.backToCourseShort")}
                            onClick={backToCourse}
                          >
                            <ArrowLeft size={20} />
                          </Button>
                          <p className={s.quizResultActionLabel}>
                            {tx("certification.quiz.backToCourseShort")}
                          </p>
                        </div>
                        {showNextSection ? (
                          <div className={s.quizResultAction}>
                            <Button
                              iconOnly
                              size="lg"
                              variant="solid"
                              color="primary"
                              aria-label={t("certification.quiz.nextSection")}
                              title={t("certification.quiz.nextSection")}
                              onClick={goToNextSection}
                            >
                              <ArrowRight size={20} />
                            </Button>
                            <p className={s.quizResultActionLabel}>
                              {tx("certification.quiz.nextSection")}
                            </p>
                          </div>
                        ) : null}
                        {showRetake ? (
                          <div className={s.quizResultAction}>
                            <Button
                              iconOnly
                              size="lg"
                              variant="solid"
                              color="primary"
                              aria-label={t("certification.quiz.retake")}
                              title={t("certification.quiz.retake")}
                              onClick={handleRetake}
                            >
                              <RotateCcw size={20} />
                            </Button>
                            <p className={s.quizResultActionLabel}>
                              {tx("certification.quiz.retake")}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {showingVideo ? (
        <div
          className={[
            s.quizBelow,
            s.quizBelowVideoAligned,
            videoChromeFadeOut || videoChromeFadeIn,
          ]
            .filter(Boolean)
            .join(" ")}
          style={fadeVarStyle}
        >
          {videoCommentaryParas.length > 0 ? (
            <div className={s.videoCommentaryPanel} role="note">
              {videoCommentaryParas.map((para, paraIndex) => (
                <p key={paraIndex} className={s.videoCommentaryText}>
                  {para}
                </p>
              ))}
            </div>
          ) : null}
          <div className={s.quizActions}>
            {transcriptParas.length > 0 ? (
              <Button
                variant="outline"
                color="neutral"
                iconRight={
                  transcriptOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                }
                aria-expanded={transcriptOpen}
                disabled={transitioning}
                onClick={() => setTranscriptOpen((open) => !open)}
              >
                {transcriptOpen
                  ? tx("certification.lesson.hideTranscript")
                  : tx("certification.lesson.showTranscript")}
              </Button>
            ) : null}
            <div className={s.quizActionsEnd}>
              <Button
                variant="solid"
                color="primary"
                iconLeft={<ClipboardList size={16} />}
                disabled={transitioning || videoGateBlocked}
                onClick={goToQuizFromVideo}
              >
                {tx("certification.lesson.startQuiz")}
              </Button>
              {videoGateBlocked ? (
                <p className={s.quizFeedbackText}>
                  {trackingUnavailable
                    ? t("certification.lesson.startQuizTrackingUnavailable")
                    : t("certification.lesson.startQuizWatchRequired", {
                        percent: String(requireWatchPercent),
                      })}
                </p>
              ) : null}
            </div>
          </div>
          {transcriptParas.length > 0 && transcriptOpen ? (
            <div
              ref={transcriptPanelRef}
              className={`${s.transcriptPanel} ${s.quizTranscriptBelow}`}
              style={
                transcriptFillHeightPx != null
                  ? { height: transcriptFillHeightPx }
                  : undefined
              }
              role="region"
              aria-label={t("certification.lesson.transcript")}
            >
              {transcriptParas.map((para, paraIndex) => (
                <p key={paraIndex} className={s.transcriptParagraph}>
                  {para}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {showingQuiz ? (
        <div className={s.quizBelow}>
          <div className={s.quizNarrow}>
            <div className={s.quizChoiceList}>
              {choiceView.choices.map((choice, choiceIndex) => {
                const isSelected = choiceView.selectedChoice === choiceIndex;
                const showWrong =
                  isSelected && choiceView.feedback === "incorrect";
                const showRight =
                  isSelected && choiceView.feedback === "correct";
                const showCorrectIcon = showRight;
                const showIncorrectIcon = showWrong;

                const motionClass =
                  motionPhase === "exiting" && motionDirection === "forward"
                    ? s.quizChoiceExitForward
                    : motionPhase === "exiting" && motionDirection === "back"
                      ? s.quizChoiceExitBack
                      : motionPhase === "entering" && motionDirection === "forward"
                        ? s.quizChoiceEnterForward
                        : motionPhase === "entering" && motionDirection === "back"
                          ? s.quizChoiceEnterBack
                          : "";

                return (
                  <button
                    key={`${motionGeneration}-${choiceIndex}`}
                    type="button"
                    onClick={() => handleSelect(choiceIndex)}
                    disabled={
                      transitioning ||
                      choiceView.selectedChoice !== null ||
                      checking
                    }
                    className={[
                      s.quizChoiceButton,
                      showWrong ? s.quizChoiceButtonIncorrect : "",
                      showRight ? s.quizChoiceButtonCorrect : "",
                      motionClass,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={
                      {
                        ["--choice-i" as string]: choiceIndex,
                        ["--choice-rev" as string]: choiceLastIndex - choiceIndex,
                      } as React.CSSProperties
                    }
                  >
                    <span className={s.quizChoiceLabel}>
                      {contentTextNode(tx, choice)}
                    </span>
                    <span
                      className={[
                        s.quizChoiceIcon,
                        showCorrectIcon ? s.quizChoiceIconCorrect : "",
                        showIncorrectIcon ? s.quizChoiceIconIncorrect : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-hidden
                    >
                      {showCorrectIcon ? <CheckCircle2 size={20} /> : null}
                      {showIncorrectIcon ? <XCircle size={20} /> : null}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              className={[s.quizActions, quizChromeFadeOut].filter(Boolean).join(" ")}
              style={fadeVarStyle}
            >
              {index === 0 && hasVideo ? (
                <Button
                  variant="outline"
                  color="neutral"
                  iconLeft={<ArrowLeft size={16} />}
                  disabled={transitioning}
                  onClick={goToVideoFromQuiz}
                >
                  {tx("certification.quiz.backToVideo")}
                </Button>
              ) : index > 0 ? (
                <Button
                  variant="outline"
                  color="neutral"
                  iconLeft={<ArrowLeft size={16} />}
                  disabled={transitioning}
                  onClick={handlePrevious}
                >
                  {tx("certification.quiz.lastQuestion")}
                </Button>
              ) : null}
              <div className={s.quizActionsEnd}>
                <Button
                  variant="solid"
                  color="primary"
                  disabled={
                    selectedChoice === null || submitting || transitioning
                  }
                  loading={submitting}
                  iconRight={
                    isLastQuestion ? <Check size={16} /> : <ArrowRight size={16} />
                  }
                  onClick={handleNext}
                >
                  {isLastQuestion
                    ? t("certification.quiz.finish")
                    : t("certification.quiz.next")}
                </Button>
              </div>
            </div>

            <div
              className={[s.quizFeedbackSlot, quizChromeFadeOut]
                .filter(Boolean)
                .join(" ")}
              style={fadeVarStyle}
              aria-live="polite"
            >
              {displayFeedback === "incorrect" ? (
                <p className={s.quizFeedbackText}>
                  {tx("certification.quiz.incorrectFeedback")}
                </p>
              ) : null}
              {displayFeedback === "correct" ? (
                <p className={`${s.quizFeedbackText} ${s.quizFeedbackCorrect}`}>
                  {tx("certification.quiz.correctFeedback")}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </QuizHeroLayout>
  );
}
